import { getCsrfToken } from "@/lib/authed-fetch";
import { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { RichTextEditor, stripTransientImageUrls } from '@/components/ui/rich-text-editor';
import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ListTodo, Loader2, ChevronDown, ChevronUp, FileText, Plus, Sun, Moon, Type, Upload, X, Paperclip, Download, Pencil, FileIcon, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import { UploadProgressDialog } from '@/components/upload-progress-dialog';
import { usePulseWorkbenchContext } from '@/contexts/pulse-workbench-context';
import { usePulseWorkbench } from '@/hooks/use-pulse-workbench';
import { WorkbenchTasksPanel } from './workbench-tasks';
import { PulseSequenceTags } from '@/components/pulse-sequence-tags';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { linkifyHtml } from '@/lib/utils';
import { neutralizeCidImagesHtml } from '@/lib/pulse-link';
import { useToast } from '@/hooks/use-toast';
import { AuthenticatedImage } from '@/components/authenticated-image';
import { EffortEntriesEditor } from '@/components/effort-entries-editor';
import { PulseReactionsInline } from '@/components/pulse-reactions';
import { CallPulseCard } from '@/components/calls/CallPulseCard';
import { ClientUploadReview } from '@/components/client-upload-review';
import { isClientUploadPulse } from '@shared/client-uploads';
import {
  CallCooperativeDialog,
  CallCooperativeDialogContent,
} from '@/components/calls/CallCooperativeDialog';
import { useCall } from '@/contexts/call-context';
import { EmailPulseParticipants } from '@/components/email-pulse-participants';
import { useAuth } from '@/contexts/auth-context';
import { PulseReplyButton } from '@/components/pulse-reply-button';
import { PulseForwardButton } from '@/components/pulse-forward-button';
import { EmailHtmlView } from '@/components/email-html-view';
import { parseApiErrorObject } from '@/lib/api-error';
import { displayedPulseContent, pulseContentSnapshot, pulseContentSnapshotSchema, type PulseContentSnapshot } from '@shared/pulse-content';

type ContentTheme = 'original' | 'white' | 'black';

interface UploadFile {
  name: string;
  size: number;
  progress: number;
  status: 'waiting' | 'uploading' | 'completed' | 'error' | 'cancelled';
}

export function WorkbenchShell() {
  const { activeCall } = useCall();
  const {
    isOpen,
    activePulseId,
    closeWorkbench,
    drafts,
    addDraft,
    isSubmitting,
    commitDrafts,
  } = usePulseWorkbenchContext();

  const { pulse, tasks, sequences, isLoading, invalidate } = usePulseWorkbench(activePulseId, {
    enabled: isOpen && activePulseId !== null,
  });

  const [contentOpen, setContentOpen] = useState(true);
  const [selectedText, setSelectedText] = useState('');
  const [selectionPosition, setSelectionPosition] = useState<{ top: number; left: number } | null>(null);
  const [contentTheme, setContentTheme] = useState<ContentTheme>('original');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const editSessionRef = useRef<{ pulseId: number; initialized: boolean; baseline?: PulseContentSnapshot } | null>(null);
  const [contentConflict, setContentConflict] = useState<PulseContentSnapshot | null>(null);
  const [retainedDrafts, setRetainedDrafts] = useState<string[]>([]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [uploadProgress, setUploadProgress] = useState<{
    open: boolean;
    files: UploadFile[];
    totalProgress: number;
  }>({ open: false, files: [], totalProgress: 0 });
  
  const contentRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeUploadsRef = useRef<Map<number, XMLHttpRequest>>(new Map());
  const imageUploadPendingRef = useRef(false);
  const [imageUploadPending, setImageUploadPending] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Task #173: user-triggered re-upload of a failed email attachment.
  const [retryingIndex, setRetryingIndex] = useState<number | null>(null);
  const retryAttachmentMutation = useMutation({
    mutationFn: async (originalIndex: number) => {
      setRetryingIndex(originalIndex);
      const response = await apiRequest('POST', `/api/pulses/${activePulseId}/attachments/${originalIndex}/retry`);
      return response.json();
    },
    onSuccess: (data: { status: string; attachment?: { filename?: string } }) => {
      invalidate();
      toast({ title: `Re-uploaded ${data.attachment?.filename || 'attachment'}` });
    },
    onError: (error: Error) => {
      invalidate();
      if ((error as any)?.status === 410 || error.message.startsWith('410')) {
        toast({
          title: 'Source email is gone',
          description: 'The original email is no longer available — please attach the file manually.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Retry failed',
          description: 'Temporary problem reaching the mailbox or Dropbox — please try again.',
          variant: 'destructive',
        });
      }
    },
    onSettled: () => setRetryingIndex(null),
  });

  // Freeze content for the entire edit session: cache refreshes (including
  // attachment uploads) must not replace a draft. Quill normalizes HTML, so
  // comparing strings to decide whether a draft is dirty is not reliable.
  // Priority: detailedNotes (Markdown) > htmlContent (Sanitized HTML) > referenceTextSection (Raw Fallback)
  useEffect(() => {
    if (!isOpen || activePulseId === null ||
        (editSessionRef.current && editSessionRef.current.pulseId !== activePulseId)) {
      editSessionRef.current = null;
      setIsEditing(false);
      setEditContent('');
      setContentConflict(null);
      setRetainedDrafts([]);
      return;
    }
    if (!isEditing) {
      editSessionRef.current = null;
      setEditContent('');
      setContentConflict(null);
      setRetainedDrafts([]);
      return;
    }
    if (!editSessionRef.current) {
      editSessionRef.current = { pulseId: activePulseId, initialized: false };
    }
    if (!editSessionRef.current.initialized && pulse?.id === activePulseId) {
      editSessionRef.current.initialized = true;
      editSessionRef.current.baseline = pulseContentSnapshot(pulse);
      setEditContent(displayedPulseContent(editSessionRef.current.baseline));
    }
  }, [isEditing, pulse, isOpen, activePulseId]);

  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !contentRef.current) {
      setSelectedText('');
      setSelectionPosition(null);
      return;
    }

    const text = selection.toString().trim();
    if (text && contentRef.current.contains(selection.anchorNode)) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = contentRef.current.getBoundingClientRect();
      
      setSelectedText(text);
      setSelectionPosition({
        top: rect.bottom - containerRect.top + 4,
        left: rect.left - containerRect.left + (rect.width / 2),
      });
    } else {
      setSelectedText('');
      setSelectionPosition(null);
    }
  }, []);

  const handleCreateTaskFromSelection = () => {
    if (selectedText) {
      addDraft(selectedText);
      setSelectedText('');
      setSelectionPosition(null);
      window.getSelection()?.removeAllRanges();
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleTextSelection);
    return () => document.removeEventListener('mouseup', handleTextSelection);
  }, [handleTextSelection]);

  const handleCommit = async () => {
    try {
      const result = await commitDrafts();
      if (result.failed.length > 0) {
        console.error('Some tasks failed to create:', result.failed);
      }
    } catch (error) {
      console.error('Failed to commit drafts:', error);
    }
  };

  // Save content mutation
  const saveContentMutation = useMutation({
    mutationFn: async ({ content, session }: { content: string; session: NonNullable<typeof editSessionRef.current> }) => {
      const response = await apiRequest('PATCH', `/api/pulses/${session.pulseId}`, {
        htmlContent: content,
        detailedNotes: content,
        expectedContent: session.baseline,
      });
      return response.json();
    },
    onSuccess: (_data, { session }) => {
      queryClient.invalidateQueries({ queryKey: ['pulse-workbench', session.pulseId] });
      if (editSessionRef.current !== session) return;
      invalidate();
      setIsEditing(false);
      toast({ title: 'Content saved successfully' });
    },
    onError: (error, { session }) => {
      if (editSessionRef.current !== session) return;
      const { status, body } = parseApiErrorObject(error);
      const current = pulseContentSnapshotSchema.safeParse(body?.currentContent);
      if (status === 409 && body?.code === 'PULSE_CONTENT_CONFLICT' && current.success) {
        setContentConflict(current.data);
        return;
      }
      toast({ title: 'Failed to save content', description: error.message, variant: 'destructive' });
    },
  });

  const handleSaveContent = () => {
    if (imageUploadPendingRef.current) {
      toast({
        title: 'Image upload in progress',
        description: 'Wait for the image upload to finish before saving.',
        variant: 'destructive',
      });
      return;
    }
    const session = editSessionRef.current;
    if (!session?.baseline || contentConflict || saveContentMutation.isPending) return;
    saveContentMutation.mutate({ content: stripTransientImageUrls(editContent), session });
  };

  const reviewLatestContent = () => {
    const session = editSessionRef.current;
    if (!session || !contentConflict || imageUploadPendingRef.current) return;
    // Keep every recovery draft until the edit session ends. Never rebase the
    // stale HTML wholesale: doing so would reintroduce the old image sources.
    setRetainedDrafts(previous => [...previous, editContent]);
    editSessionRef.current = { ...session, baseline: contentConflict };
    setEditContent(displayedPulseContent(contentConflict));
    setContentConflict(null);
  };

  const handleImageUploadPendingChange = useCallback((pending: boolean) => {
    imageUploadPendingRef.current = pending;
    setImageUploadPending(pending);
  }, []);

  // Save title mutation
  const saveTitleMutation = useMutation({
    mutationFn: async (title: string) => {
      const response = await apiRequest('PATCH', `/api/pulses/${activePulseId}`, {
        pulseDescription: title,
      });
      return response.json();
    },
    onSuccess: () => {
      invalidate();
      setIsEditingTitle(false);
      toast({ title: 'Title saved successfully' });
    },
    onError: (error) => {
      toast({ title: 'Failed to save title', description: error.message, variant: 'destructive' });
    },
  });

  const handleStartEditTitle = () => {
    setEditTitleValue(pulse?.pulseDescription || '');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = () => {
    if (editTitleValue.trim()) {
      saveTitleMutation.mutate(editTitleValue.trim());
    }
  };

  const handleCancelTitleEdit = () => {
    setIsEditingTitle(false);
    setEditTitleValue('');
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelTitleEdit();
    }
  };

  // Upload files to Dropbox
  const uploadFilesToDropbox = async (files: File[]): Promise<void> => {
    if (!activePulseId) return;

    const progressFiles: UploadFile[] = files.map(f => ({
      name: f.name,
      size: f.size,
      progress: 0,
      status: 'waiting' as const,
    }));
    
    setUploadProgress({
      open: true,
      files: progressFiles,
      totalProgress: 0,
    });
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        setUploadProgress(prev => ({
          ...prev,
          files: prev.files.map((f, idx) => 
            idx === i ? { ...f, status: 'uploading' } : f
          ),
        }));
        
        const xhr = new XMLHttpRequest();
        activeUploadsRef.current.set(i, xhr);
        
        await new Promise<void>((resolve, reject) => {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const progress = Math.round((e.loaded / e.total) * 100);
              setUploadProgress(prev => {
                const newFiles = [...prev.files];
                if (newFiles[i]) {
                  newFiles[i].progress = progress;
                }
                const totalProgress = newFiles.reduce((sum, f) => sum + f.progress, 0) / newFiles.length;
                return { ...prev, files: newFiles, totalProgress };
              });
            }
          });
          
          xhr.onload = () => {
            activeUploadsRef.current.delete(i);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          };
          
          xhr.onerror = () => {
            activeUploadsRef.current.delete(i);
            reject(new Error('Network error'));
          };
          
          xhr.onabort = () => {
            activeUploadsRef.current.delete(i);
            reject(new Error('Upload cancelled'));
          };
          
          xhr.open('POST', `/api/pulses/${activePulseId}/attachments`);
          const csrfToken = getCsrfToken();
          if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken);
          xhr.withCredentials = true;
          xhr.send(formData);
        });
        
        setUploadProgress(prev => ({
          ...prev,
          files: prev.files.map((f, idx) => 
            idx === i ? { ...f, status: 'completed', progress: 100 } : f
          ),
        }));
      } catch (error: any) {
        if (error.message === 'Upload cancelled') {
          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map((f, idx) => 
              idx === i ? { ...f, status: 'cancelled' } : f
            ),
          }));
        } else {
          console.error(`Failed to upload ${file.name}:`, error);
          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map((f, idx) => 
              idx === i ? { ...f, status: 'error' } : f
            ),
          }));
        }
      }
    }
    
    // Refresh pulse data to get new attachments
    invalidate();
    
    // Close progress dialog after delay
    setTimeout(() => {
      setUploadProgress({ open: false, files: [], totalProgress: 0 });
      activeUploadsRef.current.clear();
    }, 1500);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      uploadFilesToDropbox(files);
    }
    e.target.value = '';
  };

  const cancelUpload = (fileIndex: number) => {
    const xhr = activeUploadsRef.current.get(fileIndex);
    if (xhr) {
      xhr.abort();
    }
    setUploadProgress(prev => ({
      ...prev,
      files: prev.files.map((f, idx) => 
        idx === fileIndex ? { ...f, status: 'cancelled' } : f
      ),
    }));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Get content with proper theme styling
  // Priority: detailedNotes (Markdown) > htmlContent (Sanitized HTML) > referenceTextSection (Raw Fallback)
  // This ensures clean text is preferred over potentially messy raw email HTML
  const descriptionContent = pulse?.detailedNotes || pulse?.htmlContent || pulse?.referenceTextSection;
  const sanitizedDescription = descriptionContent 
    ? DOMPurify.sanitize(neutralizeCidImagesHtml(descriptionContent), { 
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'a', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'src', 'alt', 'width', 'height']
      })
    : null;

  // Theme-based styling
  const getContentStyles = () => {
    switch (contentTheme) {
      case 'white':
        return 'bg-slate-800 text-white prose-invert';
      case 'black':
        return 'bg-white text-black prose-stone';
      default:
        return 'bg-slate-900 text-slate-300 prose-invert';
    }
  };

  // Pulse attachments
  const attachments = (pulse?.attachments as Array<{ filename: string; path: string; size: number; contentType: string; originalIndex?: number; sharedLink?: string; status?: 'pending' | 'uploaded' | 'failed' | 'source_missing' }>) || [];
  const attachmentProcessingStatus = (pulse as any)?.attachmentProcessingStatus as 'pending' | 'processing' | 'done' | null | undefined;
  const isAttachmentProcessing = attachmentProcessingStatus === 'pending' || attachmentProcessingStatus === 'processing';


  return (
    <CallCooperativeDialog
      callActive={!!activeCall}
      open={isOpen}
      onOpenChange={(open) => !open && closeWorkbench()}
    >
      <CallCooperativeDialogContent
        className="max-w-7xl w-[90vw] h-[92vh] flex flex-col p-0 gap-0 bg-slate-900 border-slate-700"
        data-testid="pulse-workbench-dialog"
        aria-labelledby="pulse-workbench-dialog-title"
        aria-describedby="pulse-workbench-dialog-description"
      >
        <DialogHeader className="px-6 py-4 border-b border-slate-700 flex-shrink-0 pr-12">
          <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
          <DialogTitle id="pulse-workbench-dialog-title" className="text-lg font-semibold text-white">
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </span>
            ) : isEditingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editTitleValue}
                  onChange={(e) => setEditTitleValue(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  autoFocus
                  className="flex-1 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="input-edit-pulse-title"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleSaveTitle}
                  disabled={saveTitleMutation.isPending}
                  className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                  data-testid="button-save-pulse-title"
                >
                  {saveTitleMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelTitleEdit}
                  className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                  data-testid="button-cancel-pulse-title"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <span className="flex items-center gap-2 group">
                <span className="truncate">
                  {pulse?.category === 'client_files' ? 'Files from Client' :
                    (pulse?.pulseDescription || 'Pulse Workbench').split('\n')[0].slice(0, 80) +
                    ((pulse?.pulseDescription?.length || 0) > 80 ? '...' : '')}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleStartEditTitle}
                  className="h-6 w-6 p-0 text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid="button-edit-pulse-title"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              </span>
            )}
          </DialogTitle>
          {pulse?.id && pulse?.projectId && pulse?.category !== 'email_in' && (
            <div className="mt-1">
              <PulseReactionsInline
                pulseId={pulse.id}
                projectId={pulse.projectId}
                maxVisible={4}
              />
            </div>
          )}
          {pulse?.id && (
            <p className="text-xs text-slate-500 mt-1" data-testid="pulse-id-display">
              Pulse Workbench • Pulse ID: {pulse.id}
            </p>
          )}
          {pulse?.id && pulse?.projectId && (pulse?.category === 'email_in' || pulse?.category === 'email_out') && (
            <div className="mt-2">
              <EmailPulseParticipants
                pulseId={pulse.id}
                projectId={pulse.projectId}
                direction={pulse.category === 'email_out' ? 'outgoing' : 'incoming'}
                defaultClientId={typeof pulse.projectId === 'string' && pulse.projectId.includes('-')
                  ? pulse.projectId.split('-')[1]?.slice(0, 2) || null
                  : null}
              />
            </div>
          )}
          {pulse?.id && pulse?.projectId && (pulse?.category === 'email_in' || pulse?.category === 'email_out') && (
            <div className="mt-3 flex flex-wrap gap-2">
              {pulse.category === 'email_in' && (
                <PulseReplyButton pulseId={pulse.id} projectId={pulse.projectId} />
              )}
              <PulseForwardButton pulseId={pulse.id} projectId={pulse.projectId} />
            </div>
          )}
          </div>
          {/* Sequences live top-right (left of the X) to save vertical space */}
          {pulse?.projectId && pulse?.id && pulse?.category !== 'effort' && (
            <div className="flex-shrink-0 max-w-[40%]" data-testid="pulse-sequence-tags">
              <PulseSequenceTags 
                pulseId={pulse.id}
                projectId={pulse.projectId}
                showInlineAllLink={true}
                labelText="Sequences:"
              />
            </div>
          )}
          </div>
          <DialogDescription id="pulse-workbench-dialog-description" className="sr-only">
            Pulse workbench for managing tasks and chat
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div>
            <Collapsible open={contentOpen} onOpenChange={setContentOpen} className="border-b border-slate-700">
              <CollapsibleTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full flex items-center justify-between px-6 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-none"
                  data-testid="button-toggle-content"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {isClientUploadPulse(pulse) ? 'Files from Client' : pulse?.category === 'effort' ? 'Effort estimate' : 'Pulse Content'}
                  </span>
                  {contentOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
              {isClientUploadPulse(pulse) && pulse?.projectId ? (
                typeof (pulse.metadata as Record<string, unknown> | null)?.clientUploadSessionId === 'string'
                  ? <ClientUploadReview projectId={pulse.projectId} sessionId={(pulse.metadata as Record<string, unknown>).clientUploadSessionId as string} />
                  : <p className="p-6 text-sm text-amber-300">Diese Sendung hat keine gültige Sitzungsreferenz. Bitte die Projektansicht aktualisieren.</p>
              ) : pulse?.category === 'effort' ? (
                <div className="px-6 py-4">
                  <EffortEntriesEditor pulseId={pulse.id} committedAt={(pulse.committedAt as Date | string | null) as string | null || null} />
                </div>
              ) : (
              <>
                {/* Theme buttons and edit toggle */}
                <div className="px-6 py-2 flex items-center gap-2 border-b border-slate-700/50 bg-slate-800/30">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-500 mr-2">Theme:</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 w-7 p-0 ${contentTheme === 'original' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
                      onClick={() => setContentTheme('original')}
                      title="Original theme"
                      data-testid="button-theme-original"
                    >
                      <Type className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 w-7 p-0 ${contentTheme === 'white' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
                      onClick={() => setContentTheme('white')}
                      title="White text on dark"
                      data-testid="button-theme-white"
                    >
                      <Moon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 w-7 p-0 ${contentTheme === 'black' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
                      onClick={() => setContentTheme('black')}
                      title="Black text on white"
                      data-testid="button-theme-black"
                    >
                      <Sun className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex-1" />
                  
                  {/* Edit/View toggle */}
                  {!isEditing ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-slate-400 hover:text-white"
                      onClick={() => setIsEditing(true)}
                      data-testid="button-edit-content"
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-slate-400 hover:text-white"
                        onClick={() => setIsEditing(false)}
                        data-testid="button-cancel-edit"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700"
                        onClick={handleSaveContent}
                        disabled={saveContentMutation.isPending || imageUploadPending || !!contentConflict}
                        data-testid="button-save-content"
                      >
                        {saveContentMutation.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            Save
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {/* Attachment upload */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="*/*"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-slate-400 hover:text-white"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="button-upload-attachment"
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    Attach
                  </Button>
                </div>

                {/* Content display/edit area */}
                <div ref={contentRef} className="relative">
                  {isEditing ? (
                    <div 
                      className={`min-h-[180px] quill-editor-wrapper ${
                        contentTheme === 'black' 
                          ? 'quill-black-text' 
                          : contentTheme === 'white'
                            ? 'quill-white-text'
                            : ''
                      }`}
                    >
                      {contentConflict && (
                        <div role="alert" className="m-4 rounded border border-amber-500/40 p-3 text-sm">
                          <p>Content changed while you were editing, possibly because email images finished processing. Nothing was saved. Your draft is still below.</p>
                          <p className="mt-2">Review the latest version, then copy your text changes from the retained draft before saving. Keep the latest images.</p>
                          <Button className="mt-2" onClick={reviewLatestContent} disabled={imageUploadPending} data-testid="button-review-latest-content">
                            Review latest version
                          </Button>
                        </div>
                      )}
                      {retainedDrafts.map((draft, index) => (
                        <details key={index} open className="m-4 rounded border p-3" data-testid="retained-content-draft">
                          <summary>Retained draft {index + 1} — copy your text changes into the editor below</summary>
                          <EmailHtmlView enhanceImages={false} html={DOMPurify.sanitize(neutralizeCidImagesHtml(draft))} />
                          <details>
                            <summary>Original draft HTML</summary>
                            <textarea aria-label={`Retained draft ${index + 1} HTML`} readOnly value={draft} className="w-full min-h-24" />
                          </details>
                        </details>
                      ))}
                      <RichTextEditor
                        value={editContent}
                        onChange={setEditContent}
                        readOnly={saveContentMutation.isPending}
                        placeholder="Enter rich text content..."
                        showImageButton={true}
                        pulseId={activePulseId}
                        onImageUploadPendingChange={handleImageUploadPendingChange}
                      />
                    </div>
                  ) : pulse?.category === 'call' && pulse?.htmlContent ? (
                    /* Phase E.2 (#192): call pulses get a dedicated card with
                     * snapshot lightbox + admin regenerate, not the generic
                     * sanitized HTML view. `CallPulseCardPulse` is the
                     * structural subset of `Pulse` the card actually reads,
                     * compile-time-checked in CallPulseCard.tsx. */
                    <div className="px-6 py-4 pb-8" data-testid="pulse-content-call">
                      <CallPulseCard pulse={pulse} />
                    </div>
                  ) : sanitizedDescription ? (
                    <EmailHtmlView
                      key={activePulseId}
                      enhanceImages={pulse?.category === 'email_in' || pulse?.category === 'email_out'}
                      className={`px-6 py-4 text-sm prose prose-sm max-w-none select-text pb-8 ${getContentStyles()}`}
                      style={{ userSelect: 'text' }}
                      html={linkifyHtml(sanitizedDescription)}
                      data-testid="pulse-content-display"
                    />
                  ) : (
                    <p className="px-6 py-4 text-sm text-muted-foreground italic" data-testid="pulse-content-empty">
                      No content provided. Click Edit to add content.
                    </p>
                  )}
                  {selectedText && selectionPosition && !isEditing && (
                    <Button
                      size="sm"
                      onClick={handleCreateTaskFromSelection}
                      className="absolute z-50 bg-blue-600 hover:bg-blue-700 text-white shadow-lg transform -translate-x-1/2"
                      style={{ 
                        top: selectionPosition.top, 
                        left: selectionPosition.left 
                      }}
                      data-testid="button-create-task-from-selection"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Create Task
                    </Button>
                  )}
                </div>
              </>
              )}
              </CollapsibleContent>
            </Collapsible>

            {pulse?.category !== 'effort' && (
            <div className="px-6 py-4 space-y-4">
              <div>
                <h3 className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-3">
                  <Paperclip className="h-4 w-4" />
                  Attachments
                  {attachments.length > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                      {attachments.length}
                    </span>
                  )}
                  {isAttachmentProcessing && (
                    <span className="flex items-center gap-1 text-xs text-amber-400 font-normal" data-testid="attachment-processing-indicator">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Uploading…
                    </span>
                  )}
                </h3>
                {isAttachmentProcessing && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md">
                    <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
                    <span>Attachments are uploading in the background — files will appear here shortly.</span>
                  </div>
                )}
                {!isAttachmentProcessing && attachments.some(a => a.status === 'source_missing') && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-md" data-testid="attachment-source-missing-banner">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Some attachments couldn't be retrieved — the original email is no longer in the mailbox.</span>
                  </div>
                )}
                {attachments.length > 0 ? (
                  <div className="space-y-3">
                    {attachments.map((attachment, idx) => {
                      const isImage = attachment.contentType?.startsWith('image/');
                      const attStatus = attachment.status || 'uploaded';
                      const isPending = attStatus === 'pending';
                      const isFailed = attStatus === 'failed' || attStatus === 'source_missing';
                      const isReady = !isPending && !isFailed;
                      const statusLabel = attStatus === 'source_missing'
                        ? 'Source unavailable'
                        : attStatus === 'failed'
                          ? 'Upload failed'
                          : 'Uploading…';
                      
                      const handleDownload = async (e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isReady) return;
                        
                        toast({ title: `Downloading ${attachment.filename}...` });
                        
                        try {
                          const response = await fetch(`/api/pulses/${pulse?.id}/attachment/${idx}`, {
                            credentials: 'include',
                          });
                          
                          if (!response.ok) throw new Error('Download failed');
                          
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = attachment.filename;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          console.error('Download failed:', error);
                          toast({ title: 'Download failed', variant: 'destructive' });
                        }
                      };

                      // Trailing status affordance: spinner while uploading,
                      // warning triangle on failure, download icon when ready.
                      const trailingIcon = isPending ? (
                        <Loader2 className="h-4 w-4 text-amber-400 animate-spin flex-shrink-0" />
                      ) : isFailed ? (
                        <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
                      ) : (
                        <Download className="h-4 w-4 text-blue-400 group-hover:text-blue-300 flex-shrink-0" />
                      );
                      // Task #173: admins can retry a failed email upload once
                      // Dropbox has space again (strictly user-triggered).
                      const retryIndex = attachment.originalIndex ?? idx;
                      const isRetrying = retryingIndex === retryIndex;
                      const showRetry = isAdmin && attStatus === 'failed' && !isAttachmentProcessing;
                      const retryButton = showRetry ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs border-red-500/40 text-red-300 hover:text-red-200 hover:bg-red-500/10 flex-shrink-0"
                          disabled={retryAttachmentMutation.isPending}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            retryAttachmentMutation.mutate(retryIndex);
                          }}
                          data-testid={`attachment-retry-${idx}`}
                        >
                          {isRetrying ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3 w-3 mr-1" />
                          )}
                          Retry
                        </Button>
                      ) : null;
                      const metaLine = isReady ? (
                        <p className="text-slate-500 text-xs">{formatFileSize(attachment.size)}</p>
                      ) : (
                        <p className={`text-xs ${isFailed ? 'text-red-400' : 'text-amber-400'}`}>
                          {statusLabel}
                          {attachment.size ? ` · ${formatFileSize(attachment.size)}` : ''}
                        </p>
                      );
                      
                      if (isImage && isReady) {
                        return (
                          <div
                            key={idx}
                            className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden group hover:border-slate-600 cursor-pointer transition-colors"
                            onClick={handleDownload}
                            title={`Click to download ${attachment.filename}`}
                            data-testid={`attachment-item-${idx}`}
                          >
                            <AuthenticatedImage
                              src={`/api/pulses/${pulse?.id}/attachment/${idx}`}
                              alt={attachment.filename}
                              className="w-full object-contain bg-slate-900"
                            />
                            <div className="flex items-center gap-3 px-3 py-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-slate-200 truncate text-sm font-medium" title={attachment.filename}>
                                  {attachment.filename}
                                </p>
                                <p className="text-slate-500 text-xs">
                                  {formatFileSize(attachment.size)}
                                </p>
                              </div>
                              <Download className="h-4 w-4 text-blue-400 group-hover:text-blue-300 flex-shrink-0" />
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div
                          key={idx}
                          className={`flex items-center gap-3 p-3 bg-slate-800 rounded-lg border border-slate-700 text-sm transition-colors ${isReady ? 'group hover:border-slate-600 cursor-pointer' : 'cursor-default opacity-80'}`}
                          onClick={handleDownload}
                          title={isReady ? `Click to download ${attachment.filename}` : statusLabel}
                          data-testid={`attachment-item-${idx}`}
                        >
                          <div className="h-12 w-12 flex items-center justify-center bg-slate-700 rounded">
                            <FileIcon className="h-6 w-6 text-slate-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-200 truncate font-medium" title={attachment.filename}>
                              {attachment.filename}
                            </p>
                            {metaLine}
                          </div>
                          {retryButton}
                          {trailingIcon}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-slate-500">
                    <Paperclip className="h-6 w-6 mb-2 opacity-50" />
                    <p className="text-sm">No attachments yet</p>
                    <p className="text-xs mt-1">Use the Attach button in Pulse Content to add files</p>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-700 pt-4">
                <h3 className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-3">
                  <ListTodo className="h-4 w-4" />
                  Tasks ({tasks.length})
                  {drafts.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                      +{drafts.length}
                    </span>
                  )}
                </h3>
                <WorkbenchTasksPanel 
                  tasks={tasks} 
                  sequences={sequences}
                  isLoading={isLoading} 
                />
              </div>
            </div>
            )}
          </div>
        </ScrollArea>

        {drafts.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between bg-slate-800/50 flex-shrink-0">
            <span className="text-sm text-slate-400">
              {drafts.length} draft{drafts.length !== 1 ? 's' : ''} ready to create
            </span>
            <Button
              onClick={handleCommit}
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="button-commit-drafts"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                `Create ${drafts.length} Task${drafts.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </div>
        )}

        {/* Upload Progress Dialog */}
        <UploadProgressDialog
          open={uploadProgress.open}
          files={uploadProgress.files}
          totalProgress={uploadProgress.totalProgress}
          onCancel={cancelUpload}
          onCancelAll={() => {
            activeUploadsRef.current.forEach((xhr) => xhr.abort());
            setUploadProgress(prev => ({
              ...prev,
              files: prev.files.map(f => 
                f.status === 'uploading' || f.status === 'waiting' 
                  ? { ...f, status: 'cancelled' } 
                  : f
              ),
            }));
          }}
        />
      </CallCooperativeDialogContent>
    </CallCooperativeDialog>
  );
}
